import React, { useState } from "react";
import Openwatsapp from "./watsapp";

export const BookingForm = ({ setBookForm }) => {
    const [formData, setFormData] = useState({
        name: "",
        mobile: "",
        pickup: "",
        drop: "",
        car: "",
        tripType: "",
        days: "",
        customerCar: "",
        bookingType: "cab", // "cab", "rental", or "driver"
    });

    const [errors, setErrors] = useState({});
    const [showModal, setShowModal] = useState(false); // For modal visibility

    const resetForm = () => {
        setFormData({
            name: "",
            mobile: "",
            pickup: "",
            drop: "",
            car: "",
            tripType: "",
            days: "",
            customerCar: "",
            bookingType: "cab",
        });
        setErrors({}); // Clear any validation errors
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const validate = () => {
        let newErrors = {};

        // Common validations for all booking types
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!/^[0-9]{10}$/.test(formData.mobile))
            newErrors.mobile = "Enter valid 10-digit mobile number";

        // Cab booking validations
        if (formData.bookingType === "cab") {
            if (!formData.pickup.trim()) newErrors.pickup = "Pickup location required";
            if (!formData.drop.trim()) newErrors.drop = "Drop location required";
            if (!formData.car) newErrors.car = "Please select a car";
            if (!formData.tripType) newErrors.tripType = "Please select trip type";
        }

        // Rental car validations
        if (formData.bookingType === "rental") {
            if (!formData.car) newErrors.car = "Please select a car";
            if (!formData.days || formData.days < 1) newErrors.days = "Please enter valid number of days";
        }

        // Driver booking validations
        if (formData.bookingType === "driver") {
            if (!formData.pickup.trim()) newErrors.pickup = "Pickup location required";
            if (!formData.drop.trim()) newErrors.drop = "Drop location required";
            if (!formData.customerCar.trim()) newErrors.customerCar = "Please enter your car name/model";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            Openwatsapp(formData)
            setShowModal(true); // Show the modal if form is valid
        }
    };

    const getButtonGradient = () => {
        switch (formData.bookingType) {
            case "cab":
                return "bg-gradient-to-r from-yellow-600 to-red-600";
            case "rental":
                return "bg-gradient-to-r from-indigo-600 to-pink-600";
            case "driver":
                return "bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500";
            default:
                return "bg-gradient-to-r from-yellow-600 to-red-600";
        }
    };

    const getTitle = () => {
        switch (formData.bookingType) {
            case "cab":
                return "🚖 Cab Booking";
            case "rental":
                return "🚗 Car Rental Booking";
            case "driver":
                return "Book a Driver";
            default:
                return "Booking Form";
        }
    };

    const getModalMessage = () => {
        switch (formData.bookingType) {
            case "cab":
                return (
                    <>
                        <p className="text-gray-700 mt-2">
                            Hello <b>{formData.name}</b> 👋
                        </p>
                        <p className="text-gray-600 mt-2">
                            Your <b>{formData.tripType}</b> cab from
                            <br />
                            <b>{formData.pickup}</b> to <b>{formData.drop}</b>
                        </p>
                        <p className="text-sm text-gray-500 mt-3">
                            📞 Our customer care will reach you shortly!
                        </p>
                    </>
                );
            case "rental":
                return (
                    <>
                        <p className="text-gray-700">
                            Hello <b>{formData.name}</b> 👋
                        </p>
                        <p className="text-gray-600 mt-2">
                            Your <b>{formData.car}</b> is booked for{" "}
                            <b>{formData.days}</b> days.
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            Our team will contact you soon 📞
                        </p>
                    </>
                );
            case "driver":
                return (
                    <>
                        <p className="text-gray-700">
                            Hello <b>{formData.name}</b> 👋
                        </p>
                        <p className="text-gray-600 mt-2">
                            Your driver is booked from
                            <br />
                            <b>{formData.pickup}</b> to <b>{formData.drop}</b>
                        </p>
                        <p className="text-gray-600 mt-1">
                            Car: <b>{formData.customerCar}</b>
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            Driver will contact you soon 📞
                        </p>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="fixed top-0 left-0 w-full z-20 min-h-screen flex items-center justify-center p-4 backdrop-blur-sm bg-black/40">
            {/* Form Card */}
            <div className="w-full max-w-lg backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/30 bg-gradient-to-b from-slate-900 via-gray-900 to-indigo-950">
                <button
                    onClick={() => setBookForm(false)}
                    className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-red-500/80 hover:rotate-90 transition-all duration-300 text-xl font-bold"
                >
                    ×
                </button>
                <h2 className="text-3xl font-bold text-white text-center mb-6">
                    {getTitle()}
                </h2>

                {/* Booking Type Selector */}
                <div className="mb-6">
                    <select
                        name="bookingType"
                        value={formData.bookingType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white/80 focus:bg-white outline-none focus:ring-4 focus:ring-blue-300 transition mb-4"
                    >
                        <option value="cab">🚖 Cab Booking</option>
                        <option value="rental">🚗 Car Rental</option>
                        <option value="driver">👨‍✈️ Book a Driver</option>
                    </select>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name */}
                    <div>
                        <input
                            type="text"
                            name="name"
                            placeholder="👤 Full Name"
                            value={formData.name}
                            onChange={handleChange}
                            className="input"
                        />
                        {errors.name && <p className="error">{errors.name}</p>}
                    </div>

                    {/* Mobile */}
                    <div>
                        <input
                            type="tel"
                            name="mobile"
                            placeholder="📱 Mobile Number"
                            value={formData.mobile}
                            onChange={handleChange}
                            className="input"
                        />
                        {errors.mobile && (
                            <p className="error">{errors.mobile}</p>
                        )}
                    </div>

                    {/* Pickup Location - Show for cab and driver */}
                    {(formData.bookingType === "cab" ||
                        formData.bookingType === "driver") && (
                        <div>
                            <input
                                type="text"
                                name="pickup"
                                placeholder="📍 Pickup Location"
                                value={formData.pickup}
                                onChange={handleChange}
                                className="input"
                            />
                            {errors.pickup && (
                                <p className="error">{errors.pickup}</p>
                            )}
                        </div>
                    )}

                    {/* Drop Location - Show for cab and driver */}
                    {(formData.bookingType === "cab" ||
                        formData.bookingType === "driver") && (
                        <div>
                            <input
                                type="text"
                                name="drop"
                                placeholder="📍 Drop Location"
                                value={formData.drop}
                                onChange={handleChange}
                                className="input"
                            />
                            {errors.drop && (
                                <p className="error">{errors.drop}</p>
                            )}
                        </div>
                    )}

                    {/* Car Selection - Show for cab and rental */}
                    {(formData.bookingType === "cab" ||
                        formData.bookingType === "rental") && (
                        <div>
                            <select
                                name="car"
                                value={formData.car}
                                onChange={handleChange}
                                className="input"
                            >
                                <option value="">🚘 Select Car</option>
                                {formData.bookingType === "cab" && (
                                    <>
                                        <option value="Mini">Mini</option>
                                        <option value="Sedan">Sedan</option>
                                        <option value="SUV">SUV</option>
                                        <option value="Luxury">Luxury</option>
                                        <option value="Tempo Traller">Tempo Traller</option>
                                        <option value="Innova">Innova</option>
                                        <option value="Innova Crysta">Innova Crysta</option>
                                        <option value="Mini bus">Mini bus</option>
                                    </>
                                )}
                                {formData.bookingType === "rental" && (
                                    <>
                                        <option value="Sedan">Sedan</option>
                                        <option value="SUV">SUV</option>
                                        <option value="Luxury">Luxury</option>
                                        <option value="Thar">Thar</option>
                                        <option value="BMW">BMW</option>
                                        <option value="Audi">Audi</option>
                                        <option value="Event Reception Car">Event Reception Car</option>
                                        <option value="Pickup">Pickup</option>
                                    </>
                                )}
                            </select>
                            {errors.car && (
                                <p className="error">{errors.car}</p>
                            )}
                        </div>
                    )}

                    {/* Customer Car Name - Only for driver booking */}
                    {formData.bookingType === "driver" && (
                        <div>
                            <input
                                type="text"
                                name="customerCar"
                                placeholder="🚗 Your Car Name/Model"
                                value={formData.customerCar}
                                onChange={handleChange}
                                className="input"
                            />
                            {errors.customerCar && (
                                <p className="error">{errors.customerCar}</p>
                            )}
                        </div>
                    )}

                    {/* Trip Type - Only for cab */}
                    {formData.bookingType === "cab" && (
                        <div>
                            <select
                                name="tripType"
                                value={formData.tripType}
                                onChange={handleChange}
                                className="input"
                            >
                                <option value="">🔁 Trip Type</option>
                                <option value="One Way">One Way</option>
                                <option value="Round Trip">Round Trip</option>
                            </select>
                            {errors.tripType && (
                                <p className="error">{errors.tripType}</p>
                            )}
                        </div>
                    )}

                    {/* Days - Only for rental */}
                    {formData.bookingType === "rental" && (
                        <div>
                            <input
                                type="number"
                                name="days"
                                min="1"
                                placeholder="📅 Number of Days"
                                value={formData.days}
                                onChange={handleChange}
                                className="input"
                            />
                            {errors.days && (
                                <p className="error">{errors.days}</p>
                            )}
                        </div>
                    )}

                    {/* Button */}
                    <button
                        type="submit"
                        className={`w-full py-3 rounded-xl ${getButtonGradient()} text-white font-bold text-lg hover:scale-105 transition`}
                    >
                        Book Now 🚀
                    </button>
                </form>
            </div>

            {/* Animated Popup */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl p-8 w-80 text-center animate-popup">
                        <h3 className="text-2xl font-bold text-green-600 mb-2">
                            🎉{" "}"Booking Successful!"
                        </h3>
                        {getModalMessage()}

                        <button
                            onClick={() => {
                                resetForm();
                                setShowModal(false);
                                setBookForm(false);
                            }}
                            className="mt-6 px-6 py-2 bg-black text-white rounded-full font-semibold hover:scale-105 transition"
                        >
                            {formData.bookingType === "cab"
                                ? "Got it 👍"
                                : formData.bookingType === "rental"
                                ? "Awesome 👍"
                                : "Close"}
                        </button>
                    </div>
                </div>
            )}

            {/* Reusable Styles & Animation */}
            <style>
                {`
                    .input {
                        width: 100%;
                        padding: 12px 16px;
                        border-radius: 12px;
                        background: rgba(255,255,255,0.85);
                        outline: none;
                        transition: 0.3s;
                    }
                    .input:focus {
                        background: white;
                        box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.6);
                    }
                    .error {
                        color: #fee2e2;
                        font-size: 0.85rem;
                        margin-top: 4px;
                    }
                    .animate-popup {
                        animation: popup 0.4s ease-out forwards;
                    }
                    @keyframes popup {
                        0% {
                            transform: scale(0.6);
                            opacity: 0;
                        }
                        100% {
                            transform: scale(1);
                            opacity: 1;
                        }
                    }
                `}
            </style>
        </div>
    );
};
